<template>
  <article v-if="blog && variants.v1 === variant" class="tw-py-32 lg:tw-py-[200px]">
    <lazy-library-title tag="h1" :text="blog.title"></lazy-library-title>

    <div
      v-if="blog.description"
      style="padding: 20px; color: var(--white); background-color: var(--primary)"
      class="tw-my-10"
    >
      <lazy-library-text tag="span" :text="blog.description"> </lazy-library-text>
    </div>

    <div class="tw-mb-10 tw-mt-10 tw-w-full">
      <library-image :src="blog.largeImage"></library-image>
    </div>
    <div>
      <lazy-library-text :html="blog.content" class="blog-content"></lazy-library-text>
    </div>
  </article>

  <section v-if="blog && variants.v2 === variant" class="tw-pb-20 tw-pt-44 md:tw-pt-52 lg:tw-py-[200px]">
    <div class="container">
      <nuxt-link
        to="/blogs"
        :external="true"
        style="color: var(--c-primary)"
        class="n-link tw-mb-12 tw-flex tw-items-center tw-gap-3"
      >
        <span
          ><svg width="28" height="17" viewBox="0 0 28 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M21.875 9.375H6.125C5.635 9.375 5.25 8.99 5.25 8.5C5.25 8.01 5.635 7.625 6.125 7.625H21.875C22.365 7.625 22.75 8.01 22.75 8.5C22.75 8.99 22.365 9.375 21.875 9.375Z"
              fill="#7F338F"
            />
            <path
              d="M10.5 15.5C10.3852 15.5014 10.2715 15.4788 10.166 15.4336C10.0605 15.3883 9.96563 15.3216 9.8875 15.2375L3.7625 9.1125C3.4125 8.7625 3.4125 8.22 3.7625 7.87L9.8875 1.7625C10.2375 1.4125 10.78 1.4125 11.13 1.7625C11.48 2.1125 11.48 2.655 11.13 3.005L5.6175 8.5175L11.13 14.03C11.48 14.38 11.48 14.9225 11.13 15.2725C10.955 15.4475 10.7275 15.535 10.5175 15.535L10.5 15.5Z"
              fill="#7F338F"
            />
          </svg>
        </span>
        BACK TO ALL BLOGS
      </nuxt-link>
      <h2 class="tw-uppercase">{{ blog.title }}</h2>
      <div class="tw-mb-10 tw-mt-10 tw-w-full">
        <library-image
        :w-ratio="3"
        :h-ratio="2"
        :src="blog.largeImage"
        image-class="!tw-object-fill tw-w-full tw-h-full"
        style="border-radius: var(--rounded)"
        ></library-image>
      </div>
      <div v-if="blog.description" class="tw-my-10">
        <h6>{{ blog.description }}</h6>
      </div>
      <div>
        <lazy-library-text :html="blog.content" class="blog-content-2"></lazy-library-text>
      </div>
    </div>
  </section>

  <section v-if="blog && variants.v3 === variant" class="tw-pb-20 tw-pt-44 md:tw-pt-52 lg:tw-py-[200px]">
    <div class="container">
      <nuxt-link
        to="/blogs"
        :external="true"
        style="color: var(--c-primary)"
        class="n-link tw-mb-12 tw-flex tw-items-center tw-gap-3"
      >
        <span
          ><svg width="28" height="17" viewBox="0 0 28 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M21.875 9.375H6.125C5.635 9.375 5.25 8.99 5.25 8.5C5.25 8.01 5.635 7.625 6.125 7.625H21.875C22.365 7.625 22.75 8.01 22.75 8.5C22.75 8.99 22.365 9.375 21.875 9.375Z"
              fill="#E7DFBA"
            />
            <path
              d="M10.5 15.5C10.3852 15.5014 10.2715 15.4788 10.166 15.4336C10.0605 15.3883 9.96563 15.3216 9.8875 15.2375L3.7625 9.1125C3.4125 8.7625 3.4125 8.22 3.7625 7.87L9.8875 1.7625C10.2375 1.4125 10.78 1.4125 11.13 1.7625C11.48 2.1125 11.48 2.655 11.13 3.005L5.6175 8.5175L11.13 14.03C11.48 14.38 11.48 14.9225 11.13 15.2725C10.955 15.4475 10.7275 15.535 10.5175 15.535L10.5 15.5Z"
              fill="#E7DFBA"
            />
          </svg>
        </span>
        BACK TO ALL BLOGS
      </nuxt-link>
      <h2 class="tw-capitalize">{{ blog.title }}</h2>
      <div class="tw-mb-10 tw-mt-10 tw-w-full">
        <library-image :src="blog.largeImage"></library-image>
      </div>
      <div v-if="blog.description" class="blog-content-3 tw-my-10 !tw-text-white">
        <h6>{{ blog.description }}</h6>
      </div>
      <div>
        <lazy-library-text :html="blog.content" class="blog-content-3 !tw-text-white"></lazy-library-text>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import type { Component } from '~/types';
import { ComponentMixin } from '~/mixins/component.mixin';
import { BlogMixin } from '~/units/blogs/mixins/blog.mixin';
import { useBlogsStore } from '~/units/blogs/store';

export default defineNuxtComponent({
  name: 'Theme3BlogDetail',

  mixins: [ComponentMixin, BlogMixin],

  data() {
    return {
      variants: {
        v1: 'v1',
        v2: 'v2',
        v3: 'v3',
      },
    };
  },

  async setup(props: { component: Component }) {
    const blogsStore = useBlogsStore();
    await blogsStore.fetchSingleBlog(props.component);
  },

  computed: {
    backgroundColor() {
      return getVariable(this.component, 'background-color');
    },
  },
});
</script>

<style scoped>
:deep(.blog-content .p1) {
  font-size: 14px !important;
  font-family: var(--default-font) !important;
  line-height: unset !important;
  margin: 0 0 10px 0 !important;
}

:deep(.blog-content-2 p),
:deep(.blog-content-2 span) {
  font-family: var(--f-primary) !important;
}

:deep(.blog-content .p2) {
  font-size: 14px !important;
  font-family: var(--default-font) !important;
  line-height: unset !important;
  margin: 0 0 10px 0 !important;
}

:deep(.blog-content-3 h2) {
  font-size: clamp(28px, 3vw + 1rem, 36px) !important;
}

:deep(.blog-content-3 p),
:deep(.blog-content-3 span) {
  font-family: var(--f-primary) !important;
  color: white !important;
}

:deep(.blog-content-3 .p2) {
  font-size: 14px !important;
  font-family: var(--default-font) !important;
  line-height: unset !important;
  margin: 0 0 10px 0 !important;
  color: white !important;
}

:deep(.blog-content p) {
  font-size: 14px !important;
  font-family: var(--default-font) !important;
  line-height: unset !important;
  margin: 0 0 10px 0 !important;
}

:deep(.blog-content span) {
  font-size: 14px !important;
}
</style>
